import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { GALLERY } from "@/data/property";
import { useSite } from "@/context/SiteContext";
import SectionHeader from "@/components/SectionHeader";
import Lightbox from "@/components/Lightbox";

const CATS = [
  { id: "All", testid: "gallery-filter-all" },
  { id: "Exterior", testid: "gallery-filter-exterior" },
  { id: "Interior", testid: "gallery-filter-interior" },
  { id: "Bedrooms", testid: "gallery-filter-bedrooms" },
];

const SPANS = [
  "md:col-span-3 md:row-span-2",
  "md:col-span-3",
  "md:col-span-3",
  "md:col-span-2",
  "md:col-span-2",
  "md:col-span-2",
];

export default function Gallery() {
  const { media } = useSite();
  const [cat, setCat] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  const uploaded = media.map((m) => ({
    url: m.url,
    title: m.caption || "Listing photo",
    desc: "52-461 Beechwood Place, Waterloo",
  }));
  const useUploaded = uploaded.length > 0;

  const filtered = useUploaded
    ? uploaded
    : cat === "All"
      ? GALLERY
      : GALLERY.filter((g) => g.category === cat);

  const nav = (dir) =>
    setLightbox((i) => (i === null ? null : (i + dir + filtered.length) % filtered.length));

  return (
    <section id="gallery" data-testid="photo-gallery-section" className="py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeader
          eyebrow="Photo Gallery"
          title={useUploaded ? "The listing, in your own photos" : "Sample photos until yours arrive"}
          sub={
            useUploaded
              ? "Uploaded from the admin panel — the hero shot leads the page."
              : "These are sample images. Upload your real photos from the /admin panel and they replace everything here instantly."
          }
        />

        {!useUploaded && (
          <div className="mb-8 flex flex-wrap gap-2">
            {CATS.map((c) => (
              <button
                key={c.id}
                data-testid={c.testid}
                onClick={() => {
                  setCat(c.id);
                  setLightbox(null);
                }}
                className={`rounded-full border px-5 py-2 text-sm font-medium transition-colors duration-200 ${
                  cat === c.id
                    ? "border-zinc-900 bg-zinc-900 text-white dark:border-white dark:bg-white dark:text-black"
                    : "border-zinc-300 text-zinc-600 hover:border-zinc-900 hover:text-zinc-900 dark:border-zinc-700 dark:text-zinc-300 dark:hover:border-white dark:hover:text-white"
                }`}
              >
                {c.id}
              </button>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 auto-rows-[220px] sm:auto-rows-[240px] md:grid-cols-6">
          {filtered.map((g, i) => (
            <motion.button
              key={g.url}
              data-testid={`gallery-image-card-${i}`}
              onClick={() => setLightbox(i)}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, delay: (i % 6) * 0.06 }}
              className={`group relative h-full min-h-[220px] w-full overflow-hidden rounded-2xl ${useUploaded && i === 0 ? "md:col-span-3 md:row-span-2" : useUploaded ? "md:col-span-2" : SPANS[i % SPANS.length]}`}
            >
              <img
                src={g.url}
                alt={g.title}
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent opacity-80 transition-opacity duration-300 group-hover:opacity-100" />
              <div className="absolute inset-x-0 bottom-0 p-5 text-left">
                <span className="mb-2 inline-block rounded-full bg-white/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white backdrop-blur-sm">
                  {useUploaded ? (i === 0 ? "Hero" : "Listing Photo") : g.category}
                </span>
                <h3 className="translate-y-1 font-display text-lg font-semibold text-white transition-transform duration-300 group-hover:translate-y-0">
                  {g.title}
                </h3>
                <p className="mt-1 max-h-0 overflow-hidden text-sm text-white/80 transition-all duration-300 group-hover:max-h-16">
                  {g.desc}
                </p>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {lightbox !== null && (
          <Lightbox items={filtered} index={lightbox} onClose={() => setLightbox(null)} onNav={nav} />
        )}
      </AnimatePresence>
    </section>
  );
}
